/**
 * Archetypes — the "archetype" trait, and the skeleton the creature is built from.
 *
 * Every bird in the collection is the same parametric drawing: tail, legs, body,
 * wing, neck, head, beak, eye. An archetype does not change *which* parts exist, it
 * changes their proportions — and proportion is the whole of bird identity. A wader
 * is a robin with the legs and neck tripled; an owl is a robin whose head grew until
 * the neck disappeared. Building it this way means one rasteriser serves six
 * silhouettes, and the on-chain cost is a struct of ranges rather than six drawings.
 *
 * **Units.** Every length is Q8 sub-cell (`SUB` = 256), measured in glyph *cells*,
 * so 1024 is four cells. Angles are in `TRIG_STEPS` units (256 to a full turn), and
 * signed. Fractions labelled 0..256 are 1/256 of the thing they scale.
 *
 * **Ranges, not values.** Each field is an inclusive `[min, max]` drawn per token
 * from the archetype's own RNG stream. Two robins are visibly different birds; both
 * are unmistakably robins. Where a field is fixed for an archetype the range is
 * degenerate, which costs nothing and keeps the table uniform.
 *
 * The numbers below were set against the 32x32 grid: a bird occupies roughly 12 to
 * 20 cells of height and 9 to 17 of width, standing on the lower third, which leaves
 * the top half of the frame for terrain and the glitch layers to work in.
 */

import { SUB } from "../constants.js";

export type Range = readonly [min: number, max: number];

export interface Archetype {
  readonly name: string;

  /** Body ellipse half-width and half-height, Q8 cells. */
  readonly bodyW: Range;
  readonly bodyH: Range;
  /**
   * Body tilt, signed trig steps. Negative pitches the breast up (alert), positive
   * pitches it down (feeding). A wader hunting and an owl at rest differ mostly here.
   */
  readonly lean: Range;

  /** Head radius, Q8 cells. */
  readonly headR: Range;
  /** Distance from body top to head centre, Q8 cells. 0 fuses head to body. */
  readonly neckLen: Range;
  /** Neck angle from vertical, signed trig steps. */
  readonly neckAngle: Range;

  /** Beak length forward from the head, Q8 cells. */
  readonly beakLen: Range;
  /** Beak half-depth at its base, Q8 cells. Governs conical vs needle. */
  readonly beakDepth: Range;
  /** Downward hook, 0..256 of `beakLen`. A raptor is the only heavy user. */
  readonly beakHook: Range;

  /**
   * Eye radius, Q8 cells. The eye is the one feature allowed to be unrealistic.
   *
   * Held at or under half the archetype's minimum `headR`, so the eye disc — drawn at
   * 0.45 of the head's radius off its centre — is always wholly inside the head. Every
   * value here is also over a full half-cell: measured at the first, politer set (Robin
   * 88..128 Q8) every archetype's eye came out as exactly one cell whatever it authored,
   * so the field was a label on nothing and an Owl's eye was a Finch's.
   */
  readonly eyeR: Range;

  /** Tail length from the body's rear, Q8 cells. */
  readonly tailLen: Range;
  /** Tail angle below horizontal, signed trig steps. */
  readonly tailAngle: Range;
  /** Fan half-angle of the tail feathers, trig steps. 0 draws a single spike. */
  readonly tailSpread: Range;

  /** Leg length from body underside to ground, Q8 cells. */
  readonly legLen: Range;
  /** Horizontal separation of the two legs, Q8 cells. */
  readonly legSpread: Range;
  /** Foot span forward and back from the ankle, Q8 cells. */
  readonly footSpan: Range;

  /** Folded wing length along the body, Q8 cells. */
  readonly wingLen: Range;
  /** How far the wing tip drops below the body's midline, Q8 cells. */
  readonly wingDrop: Range;
  /** Primary feathers drawn at the wing tip. Costs one line each. */
  readonly primaries: Range;

  /** Crest or tuft height above the head, Q8 cells. 0 means none. */
  readonly crestLen: Range;

  /**
   * How far the breast patch spreads over the body, 0..256. This is the trait's
   * loudest visual consequence, because the breast slot is the loudest colour.
   */
  readonly breastExtent: Range;
  /**
   * Feather fluff, 0..256. Drives how strongly per-cell noise dithers the plumage
   * ramp index, so a high value reads as down and a low one as sleek.
   */
  readonly puff: Range;

  readonly weight: number;
}

export const ARCHETYPES: readonly Archetype[] = [
  {
    // The namesake and the baseline every other archetype is a deformation of.
    // Round, upright, short-tailed, with the breast carried high and wide.
    name: "Robin",
    bodyW: [960, 1216],
    bodyH: [896, 1120],
    lean: [-5, 4],
    headR: [512, 632],
    neckLen: [96, 224],
    neckAngle: [-8, 6],
    beakLen: [272, 384],
    beakDepth: [120, 176],
    beakHook: [8, 32],
    eyeR: [184, 256],
    tailLen: [896, 1152],
    tailAngle: [18, 40],
    tailSpread: [10, 22],
    legLen: [544, 736],
    legSpread: [160, 288],
    footSpan: [176, 256],
    wingLen: [880, 1120],
    wingDrop: [96, 224],
    primaries: [3, 5],
    crestLen: [0, 0],
    breastExtent: [168, 224],
    puff: [128, 208],
    weight: 22,
  },
  {
    // Small and compact: the smallest body, the shortest tail, and a conical seed
    // beak that is deep rather than long. Reads as a bird at half scale — and it has
    // to, because at 32x32 a proportion that differs from Robin's by less than a full
    // cell is a difference nobody can see. Measured separation drove these numbers
    // down from an earlier, politer set that sat 0.9 cells from Robin.
    name: "Finch",
    bodyW: [640, 832],
    bodyH: [576, 768],
    lean: [-4, 6],
    headR: [384, 480],
    neckLen: [48, 144],
    neckAngle: [-6, 8],
    beakLen: [160, 232],
    beakDepth: [152, 216],
    beakHook: [0, 16],
    eyeR: [144, 192],
    tailLen: [576, 768],
    tailAngle: [22, 46],
    tailSpread: [16, 30],
    legLen: [352, 512],
    legSpread: [112, 208],
    footSpan: [128, 184],
    wingLen: [576, 768],
    wingDrop: [48, 144],
    primaries: [3, 4],
    // Reaches past the drawing's half-cell legibility floor on purpose: authored at 96 Q8
    // this range drew a crest on none of 233 sampled Finches, so it described nothing.
    crestLen: [0, 208],
    breastExtent: [136, 200],
    puff: [144, 224],
    weight: 16,
  },
  {
    // Long and low, with the longest wedge tail in the set and a visible neck at
    // rest. Held deliberately *shallower* than Raptor: the two are the same rough
    // size, so the pair is separated by silhouette shape — Corvid runs horizontal,
    // Raptor vertical — rather than by bulk, which would read as noise.
    name: "Corvid",
    bodyW: [1088, 1312],
    bodyH: [896, 1120],
    lean: [-8, 2],
    headR: [552, 688],
    neckLen: [192, 352],
    neckAngle: [-12, 4],
    beakLen: [416, 576],
    beakDepth: [168, 232],
    beakHook: [16, 48],
    eyeR: [176, 264],
    tailLen: [1280, 1664],
    tailAngle: [8, 26],
    tailSpread: [6, 16],
    legLen: [608, 800],
    legSpread: [192, 320],
    footSpan: [208, 296],
    wingLen: [1152, 1440],
    wingDrop: [128, 288],
    primaries: [4, 6],
    // Same reason as Finch: 64 Q8 was under the floor and never drew. A jay's short crest.
    crestLen: [0, 176],
    breastExtent: [104, 160],
    puff: [96, 168],
    weight: 12,
  },
  {
    // Upright, broad-shouldered, hooked. The lean range is entirely negative: a
    // raptor at rest still holds its breast forward, and that posture plus the hook
    // is what separates it from a corvid at a glance. The deepest body and the
    // longest wing, against Corvid's longest tail.
    name: "Raptor",
    bodyW: [1216, 1472],
    bodyH: [1152, 1408],
    lean: [-14, -4],
    headR: [520, 640],
    neckLen: [80, 208],
    neckAngle: [-6, 6],
    beakLen: [288, 400],
    beakDepth: [184, 248],
    beakHook: [144, 224],
    eyeR: [192, 260],
    tailLen: [1024, 1280],
    tailAngle: [12, 30],
    tailSpread: [8, 20],
    legLen: [576, 768],
    legSpread: [288, 416],
    footSpan: [288, 400],
    wingLen: [1216, 1504],
    wingDrop: [256, 448],
    primaries: [5, 7],
    crestLen: [0, 0],
    breastExtent: [144, 208],
    puff: [80, 152],
    weight: 10,
  },
  {
    // Legs and neck tripled, body narrowed, tail almost gone. The tallest bird in
    // the collection and the only one whose body sits above the frame's midline.
    name: "Wader",
    bodyW: [832, 1024],
    bodyH: [704, 896],
    lean: [-6, 10],
    headR: [416, 512],
    neckLen: [768, 1216],
    neckAngle: [-24, 20],
    beakLen: [512, 768],
    beakDepth: [88, 136],
    beakHook: [0, 40],
    eyeR: [152, 208],
    tailLen: [448, 672],
    tailAngle: [26, 52],
    tailSpread: [8, 18],
    legLen: [1216, 1728],
    legSpread: [96, 224],
    footSpan: [224, 320],
    wingLen: [832, 1088],
    wingDrop: [64, 176],
    primaries: [3, 5],
    crestLen: [0, 160],
    breastExtent: [96, 152],
    puff: [64, 136],
    weight: 9,
  },
  {
    // The head grew until the neck disappeared. Largest head and eye, no tail to
    // speak of, legs hidden under the body. Structurally the furthest from Robin
    // while using the identical rasteriser.
    name: "Owl",
    bodyW: [1088, 1344],
    bodyH: [1024, 1248],
    lean: [-3, 3],
    headR: [744, 928],
    neckLen: [0, 48],
    neckAngle: [-4, 4],
    beakLen: [160, 240],
    beakDepth: [144, 200],
    beakHook: [88, 152],
    eyeR: [280, 368],
    tailLen: [384, 608],
    tailAngle: [34, 60],
    tailSpread: [12, 26],
    legLen: [288, 448],
    legSpread: [176, 288],
    footSpan: [192, 272],
    wingLen: [1024, 1280],
    wingDrop: [160, 320],
    primaries: [4, 6],
    crestLen: [128, 288],
    breastExtent: [176, 240],
    puff: [192, 256],
    weight: 8,
  },
] as const;

export const ARCHETYPE_WEIGHTS: readonly number[] = ARCHETYPES.map((a) => a.weight);

/**
 * Cells a bird may occupy in either axis, worst case.
 *
 * 26 of the grid's 32 leaves at least three cells of frame on every side, which is
 * what the glitch layers displace into. A slice that shifts a row by two cells must
 * not push the beak off the canvas.
 */
export const MAX_EXTENT_CELLS = 26;
const EXTENT_BUDGET = MAX_EXTENT_CELLS * SUB;

/** Every `Range`-typed field on `Archetype`, so guards and traits can iterate. */
export const ARCHETYPE_RANGE_FIELDS = [
  "bodyW",
  "bodyH",
  "lean",
  "headR",
  "neckLen",
  "neckAngle",
  "beakLen",
  "beakDepth",
  "beakHook",
  "eyeR",
  "tailLen",
  "tailAngle",
  "tailSpread",
  "legLen",
  "legSpread",
  "footSpan",
  "wingLen",
  "wingDrop",
  "primaries",
  "crestLen",
  "breastExtent",
  "puff",
] as const satisfies readonly (keyof Archetype)[];

export type ArchetypeRangeField = (typeof ARCHETYPE_RANGE_FIELDS)[number];

/**
 * The range fields grouped by unit, because a guard that compares a Q8 length to a
 * 0..256 fraction is comparing nothing. Raptor differs from Robin by 164 in
 * `beakHook`, which is a large difference in hook and a meaningless one in cells.
 */
export const LENGTH_FIELDS = [
  "bodyW",
  "bodyH",
  "headR",
  "neckLen",
  "beakLen",
  "beakDepth",
  "eyeR",
  "tailLen",
  "legLen",
  "legSpread",
  "footSpan",
  "wingLen",
  "wingDrop",
  "crestLen",
] as const satisfies readonly ArchetypeRangeField[];

/** Signed, in `TRIG_STEPS` units. */
export const ANGLE_FIELDS = ["lean", "neckAngle", "tailAngle", "tailSpread"] as const satisfies
  readonly ArchetypeRangeField[];

/** Unsigned, in 1/256 of whatever they scale. */
export const FRACTION_FIELDS = ["beakHook", "breastExtent", "puff"] as const satisfies
  readonly ArchetypeRangeField[];

/**
 * How far apart two archetypes must sit on at least one *length*, in Q8 cells.
 *
 * One full cell. Below that the difference cannot survive quantisation onto a 32x32
 * glyph grid, so the trait would be a label on nothing. This caught two pairs that
 * looked fine in the table: Robin/Finch sat 0.88 cells apart and Robin/Raptor 0.91,
 * which is why Finch was shrunk and Raptor deepened.
 */
export const MIN_ARCHETYPE_SEPARATION = SUB;

/** Midpoint of a range, used for separation. Halves are fine: this never renders. */
function mid(r: Range): number {
  return (r[0] + r[1]) / 2;
}

/** Widest gap between two archetypes on any single length field, in Q8 cells. */
export function separation(a: Archetype, b: Archetype): { field: ArchetypeRangeField; gap: number } {
  let best: { field: ArchetypeRangeField; gap: number } = { field: "bodyW", gap: -1 };
  for (const f of LENGTH_FIELDS) {
    const gap = Math.abs(mid(a[f]) - mid(b[f]));
    if (gap > best.gap) best = { field: f, gap };
  }
  return best;
}

/**
 * Structural guard invoked by tests.
 *
 * Two assertions here are the ones that actually catch authoring mistakes. A bird
 * drawn at every field's maximum must still fit the grid, or an archetype widened by
 * two cells shows up as a cropped tail on a few hundred tokens. And every pair of
 * archetypes must be separated by at least one full cell on some length, or a trait
 * value is a name with no visual consequence.
 */
export function validateArchetypes(): void {
  const seen = new Set<string>();

  // Unit grouping must stay exhaustive, otherwise a new field silently escapes the
  // separation check.
  const grouped = new Set<string>([...LENGTH_FIELDS, ...ANGLE_FIELDS, ...FRACTION_FIELDS, "primaries"]);
  for (const f of ARCHETYPE_RANGE_FIELDS) {
    if (!grouped.has(f)) throw new Error(`field "${f}" is not assigned to a unit group`);
  }

  for (const a of ARCHETYPES) {
    if (seen.has(a.name)) throw new Error(`duplicate archetype: ${a.name}`);
    seen.add(a.name);
    if (a.weight <= 0) throw new Error(`${a.name}: weight must be positive`);

    for (const field of ARCHETYPE_RANGE_FIELDS) {
      const [lo, hi] = a[field];
      if (!Number.isInteger(lo) || !Number.isInteger(hi)) {
        throw new Error(`${a.name}.${field}: bounds must be integers`);
      }
      if (lo > hi) throw new Error(`${a.name}.${field}: min ${lo} exceeds max ${hi}`);
    }

    // Lengths cannot be negative; only these four fields are signed.
    const signed = new Set<ArchetypeRangeField>(["lean", "neckAngle", "tailAngle", "tailSpread"]);
    for (const field of ARCHETYPE_RANGE_FIELDS) {
      if (signed.has(field)) continue;
      if ((a[field][0] as number) < 0) throw new Error(`${a.name}.${field}: negative length`);
    }

    if (a.primaries[0] < 1) throw new Error(`${a.name}: needs at least one primary feather`);
    for (const field of ["breastExtent", "puff", "beakHook"] as const) {
      if (a[field][1] > 256) throw new Error(`${a.name}.${field}: fraction exceeds 256`);
    }
    if (a.eyeR[1] >= a.headR[0]) throw new Error(`${a.name}: eye can exceed the head it sits in`);

    const { width, height } = maxExtent(a);
    if (width > EXTENT_BUDGET) {
      throw new Error(
        `${a.name}: worst-case width ${width} Q8 (${(width / SUB).toFixed(1)} cells)`
          + ` exceeds the ${MAX_EXTENT_CELLS}-cell budget`,
      );
    }
    if (height > EXTENT_BUDGET) {
      throw new Error(
        `${a.name}: worst-case height ${height} Q8 (${(height / SUB).toFixed(1)} cells)`
          + ` exceeds the ${MAX_EXTENT_CELLS}-cell budget`,
      );
    }
  }

  for (let i = 0; i < ARCHETYPES.length; i++) {
    for (let j = i + 1; j < ARCHETYPES.length; j++) {
      const a = ARCHETYPES[i] as Archetype;
      const b = ARCHETYPES[j] as Archetype;
      const { field, gap } = separation(a, b);
      if (gap < MIN_ARCHETYPE_SEPARATION) {
        throw new Error(
          `${a.name} and ${b.name} are only ${(gap / SUB).toFixed(2)} cells apart`
            + ` at their widest (${field}); they would render as the same bird`,
        );
      }
    }
  }
}

/**
 * Conservative upper bound on how much space a bird of this archetype can need.
 *
 * It sums each field's independent maximum, which no single token actually draws —
 * the tail is measured from the body's rear and the beak from the head's front, so a
 * real layout overlaps them. Overstating is the point: the budget check should refuse
 * an archetype that *could* crop, not one that usually does not.
 */
export function maxExtent(a: Archetype): { width: number; height: number } {
  const width = 2 * a.bodyW[1] + a.tailLen[1] + a.beakLen[1] + a.headR[1];
  const height =
    a.legLen[1] + 2 * a.bodyH[1] + a.neckLen[1] + 2 * a.headR[1] + a.crestLen[1];
  return { width, height };
}
